import { Badge } from '@common/ui/Badge';
import { Button } from '@common/ui/buttons/Button';
import { Card } from '@common/ui/Card';
import { Separator } from '@common/ui/Separator';

import { type CurrentBillingSubscription } from '@services/PaymentsService';

import styles from './SubscriptionCard.module.scss';
import {
  canCancelSubscription,
  formatAmount,
  formatDate,
  formatPaymentMethod,
  formatPlanName,
  getSubscriptionStatusView,
} from '../../utils';

export interface SubscriptionCardProps {
  subscription: CurrentBillingSubscription;
  onCancel: () => void;
}

export const SubscriptionCard = ({
  subscription,
  onCancel,
}: SubscriptionCardProps) => {
  const statusView = getSubscriptionStatusView(subscription.status);
  const expiresAt = formatDate(subscription.expiresAt);
  const nextChargeAt = subscription.autoRenewing
    ? formatDate(subscription.nextChargeAt)
    : null;
  const nextChargeAmount =
    subscription.autoRenewing &&
    subscription.amount !== null &&
    subscription.amount !== undefined
      ? formatAmount(subscription.amount, subscription.currency)
      : null;
  const paymentMethod = formatPaymentMethod(subscription.paymentMethod);
  const showCancelButton = canCancelSubscription(subscription);

  return (
    <Card testId="payments-subscription" className={styles.subscriptionCard}>
      <div className={styles.header}>
        <div className={styles.title} data-test-id="payments-subscription-plan">
          {formatPlanName(subscription.planName)}
        </div>
        <Badge
          testId="payments-subscription-status"
          color={statusView.color}
          text={statusView.text}
        />
      </div>

      {!subscription.autoRenewing && (
        <div
          className={styles.notice}
          data-test-id="payments-subscription-not-renewing"
        >
          Подписка не продлится
        </div>
      )}

      <div className={styles.rows}>
        {expiresAt && (
          <div
            className={styles.row}
            data-test-id="payments-subscription-expires"
          >
            <span className={styles.label}>
              {subscription.autoRenewing
                ? 'Активна до'
                : 'Доступ сохранится до'}
            </span>
            <span className={styles.value}>{expiresAt}</span>
          </div>
        )}
        {nextChargeAt && (
          <div
            className={styles.row}
            data-test-id="payments-subscription-next-charge"
          >
            <span className={styles.label}>Дата следующего списания</span>
            <span className={styles.value}>{nextChargeAt}</span>
          </div>
        )}
        {nextChargeAmount && (
          <div
            className={styles.row}
            data-test-id="payments-subscription-amount"
          >
            <span className={styles.label}>Сумма списания</span>
            <span className={styles.value}>{nextChargeAmount}</span>
          </div>
        )}
        {paymentMethod && (
          <div
            className={styles.row}
            data-test-id="payments-subscription-payment-method"
          >
            <span className={styles.label}>Способ оплаты</span>
            <span className={styles.value}>{paymentMethod}</span>
          </div>
        )}
      </div>

      {showCancelButton && (
        <>
          <Separator />
          <Button
            testId="payments-subscription-cancel-button"
            className={styles.button}
            variant="negativeSecondary"
            onClick={onCancel}
          >
            Отменить подписку
          </Button>
        </>
      )}
    </Card>
  );
};
